import { useIsFetching } from "@tanstack/react-query";
/**
 * `demo/guide/DemoGuide.tsx` — the guided demo's step-by-step guide (#2824).
 *
 * Mounts once in the app shell. It shows only inside a demo session, never for a
 * normal account. It builds its chapters from `chapters.ts` and the
 * considerations of the scenario the visitor picked, keeps the place per
 * demo token (`guideState.ts`) and draws each step with the spotlight cut-out and
 * card shared with the first-visit tour (`tour/SpotlightFrame.tsx`).
 *
 * The introduction shows first. After it, the guide shows the tour of the
 * page the visitor is on, the first time they open that page; a page tour
 * never moves the visitor to another page. A step is done by Next or by the
 * visitor pressing the highlighted link (the address moves on). Skip and
 * Escape ask first, then close that tour and keep its place. Help → "Show the
 * guide" opens a chooser (`requestDemoGuideChooser`). A step whose element is
 * missing falls back to its fallback anchor after the wait, so no step is ever
 * stuck.
 */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";

import type { DemoChangeToMake } from "../../../api/useDemoChangeToMake";
import { useDemoSessionState } from "../../../api/useDemoSessionState";
import { solveChangesStillComing, useDemoSolveChanges } from "../../../api/useDemoSolveChanges";
import {
  closeDemoPhone,
  isDemoPhoneOpen,
  setDemoPhoneScenarioLock,
  subscribeDemoPhoneOpen,
} from "../../../lib/demoPhone";
import { isGuidedDemo, readDemoSession } from "../../../lib/demoSession";
import { closeSidePanels, sidePanelOnPage } from "../../../lib/sidePanels";
import { useDemoScenarios } from "../../../routes/demo/demoScenarios";
import { SpotlightFrame } from "../../tour/SpotlightFrame";
import { isAnchorShown } from "../../tour/tourSteps";
import { Button } from "../../ui";
import { GuideChooser } from "./GuideChooser";
import {
  ACTION_GIVE_UP_MS,
  AUTOPLAY_STEP_MS,
  OPEN_PAUSE_MS,
  ROUTE_GIVE_UP_MS,
  SOLVE_GIVE_UP_MS,
  afterSidePanelsClose,
  autoplayPauseMs,
  firstPressTarget,
  isAutoplayClick,
  performAction,
  prefersReducedMotion,
  pressAndWait,
  scenarioButton,
  scenarioDialogOpen,
  scenarioLocked,
} from "./autoplay";
import {
  ARRIVE_SHARE,
  followStep,
  pointerArrival,
  pointerBusy,
  pointerBusyMs,
  resetPointerForTests,
  setBarEndsAt,
  setPointerMode,
} from "./autoplayPointer";
import { type ChapterMap, INTRO_ID, buildChapters, scenarioTourId } from "./chapters";
import {
  type AutoplayTarget,
  notifyDemoGuideAction,
  notifyDemoPhoneScenarioStart,
  requestDemoGuideAutoplay,
  showDemoPhonePerson,
  subscribeDemoGuideAction,
  subscribeDemoGuideAutoplay,
  subscribeDemoGuideChooser,
  subscribeDemoGuideScenario,
  subscribeDemoGuideScenarioPending,
} from "./events";
import {
  type GuideState,
  activeTourId,
  canGoBack,
  currentStep,
  goBack,
  goNext,
  initialGuideState,
  loadGuideState,
  pageTourId,
  placeOf,
  reopen,
  saveGuideState,
  skip,
  startScenario,
  stepCount,
  stepNumber,
} from "./guideState";
import { makeChangeDetail, renderMarks } from "./marks";
import type { GuideContext, GuideStep } from "./types";

/**
 * How long a step waits for its anchor, once the page has no data on the way,
 * before it shows as a centred card. The last card stays meanwhile.
 */
export const ANCHOR_WAIT_MS = 1500;
/** The longest a step waits for its anchor, data on the way or not. */
export const ANCHOR_WAIT_MAX_MS = 6000;
/**
 * A found anchor still waits while the page loads data (the rows that come in
 * move it), at most this long after it was found.
 */
export const DATA_WAIT_MAX_MS = 2000;

export function DemoGuide() {
  // localStorage is shared by every tab: only a demo tab mounts the guide, so a
  // real lead's tab never asks for the demo scenarios (#2824).
  if (!isGuidedDemo()) return null;
  const session = readDemoSession();
  if (!session) return null;
  return <GuideLoader key={session.token} session={session} />;
}

/** Waits for the scenario list, so the Setup tour has its steps before it opens. */
function GuideLoader({ session }: { session: NonNullable<ReturnType<typeof readDemoSession>> }) {
  const scenarios = useDemoScenarios();
  const considerations = scenarios.data?.find((s) => s.name === session.scenario)?.considerations;
  // The stored changes (with who and when) come from the demo session itself.
  const sessionState = useDemoSessionState();
  const changes = sessionState.data?.changes;
  const chapters = useMemo(
    () => buildChapters(considerations ?? [], changes ?? []),
    [considerations, changes],
  );
  if (scenarios.isPending || sessionState.isPending) return null;
  return (
    <GuideRuntime
      token={session.token}
      teamId={session.team_id}
      cycleId={session.cycle_id}
      chapters={chapters}
      toMake={sessionState.data?.change_status === "to_make" ? sessionState.data.to_make : null}
    />
  );
}

/**
 * How long, after the address changes, an element that was on the page before
 * the change still counts as old. The page swap can lag behind the address
 * (a lazy page, a pending render): the old page's element must never be lit.
 */
/** The longest a step waits, dimmed, for its page to open. */
export const NAV_WAIT_MS = 500;

export const STALE_GRACE_MS = 300;

/** The anchor elements that were on the page before the last address change. */
interface StaleAnchors {
  elements: Set<Element>;
  since: number;
}

/** True for an element of the old page, shortly after the address changed. */
type IgnoreAnchor = (el: Element) => boolean;

function staleIgnore(stale: StaleAnchors): IgnoreAnchor {
  return (el) =>
    stale.elements.has(el) && el.isConnected && performance.now() - stale.since < STALE_GRACE_MS;
}

/**
 * The open side drawer (#2824: the station review over the drawer). The card
 * keeps clear of it, also when the lit element sits inside it (#2824: Floor /
 * session in the station drawer), so the card never covers the drawer.
 */
export function drawerToAvoid(lit: HTMLElement | null): HTMLElement | null {
  if (!lit) return null;
  // A SideDrawer panel, or the station drawer of Setup (its own panel).
  const drawers = Array.from(
    document.querySelectorAll<HTMLElement>('.motion-drawer, [data-testid^="station-drawer-"]'),
  );
  return (
    drawers.find((d) => {
      const r = d.getBoundingClientRect();
      return r.width > 0 && r.height > 0 && !lit.contains(d);
    }) ?? null
  );
}

/** The open demo phone: the card and the lit area stay clear of it. */
function phonePanel(): HTMLElement | null {
  return document.querySelector<HTMLElement>('[data-testid="demo-phone-view"]');
}

/** True when the step points at the phone (its Phone view button or the open phone). */
export function stepNeedsPhone(step: GuideStep): boolean {
  return [...(step.anchors ?? []), ...(step.fallbackAnchors ?? [])].some(
    (name) => name === "demo-phone" || name === "demo-phone-toggle",
  );
}

/**
 * Autoplay counts a step's pause from the frame its card lands. A card that
 * never lands (a dialog stays open over it) starts the pause after this long.
 */
export const SHOW_GIVE_UP_MS = 8000;

/**
 * Autoplay's press goes this much before ARRIVE_SHARE of the bar at the
 * latest (#2962), when the pointer's glide ends. The press itself waits for
 * the pointer to arrive.
 */
const PRESS_MARGIN_MS = 60;

/**
 * Where the Autoplay pointer rests for a step: the control the first press
 * will hit (#2962). It goes there as the bar starts, so the press lands as the
 * bar ends.
 */
export function pointerTarget(
  step: GuideStep,
  el: HTMLElement | null,
  memberId?: string | null,
  toMake?: DemoChangeToMake | null,
): HTMLElement | null {
  if (!el) return null;
  if (step.done.type === "route") {
    return el.closest<HTMLElement>("a") ?? el.querySelector<HTMLElement>("a") ?? el;
  }
  if (step.done.type === "action") {
    // A press target with no box (the Publish item of a closed More menu): the
    // pointer rests on what the ring is on (#2962).
    const target = firstPressTarget(step, memberId, toMake);
    return target && hasBox(target) ? target : el;
  }
  return el;
}

function hasBox(el: HTMLElement): boolean {
  const r = el.getBoundingClientRect();
  return r.width > 0 || r.height > 0;
}

/**
 * The control a step's first press hits (#2962), or null when the step
 * presses nothing. The pointer reads it every frame from the step's landing
 * on, and glides to it the frame it shows: a nav link, Publish or More, a
 * control inside the phone.
 */
export function pressTargetOf(
  step: GuideStep,
  el: HTMLElement | null,
  memberId?: string | null,
  toMake?: DemoChangeToMake | null,
): HTMLElement | null {
  if (step.done.type === "route") {
    if (!el) return null;
    return el.closest<HTMLElement>("a") ?? el.querySelector<HTMLElement>("a") ?? el;
  }
  if (step.done.type === "action") return firstPressTarget(step, memberId, toMake);
  return null;
}

/**
 * True when Autoplay presses something for the step (#2962): a route step
 * (its link, or the page directly) and an action step. It presses as soon as
 * the pointer arrives on the target, at the latest at ARRIVE_SHARE of the bar. A
 * step that only reads presses nothing and moves on as its bar ends.
 */
export function stepPressesOnArrival(step: GuideStep): boolean {
  const done = step.done;
  if (done.type === "route") return true;
  if (done.type !== "action") return false;
  // Publish opened the phone already: the step only reads.
  if (done.action === "phone-opened" && document.querySelector('[data-testid="demo-phone-view"]'))
    return false;
  // Change the scenario before a solve is locked: the step only reads (#2962).
  if (done.action === "scenario-opened") return !scenarioLocked();
  return true;
}

/** The longest the guide stays hidden for a change that never answers. */
const CHANGE_WAIT_MS = 30_000;

/** The longest an "On the phone" step waits for the phone to open before its card lands anyway. */
export const PHONE_OPEN_WAIT_MS = 4000;

/** The longest the guide waits for the phone to fold before it shows its card anyway. */
export const PHONE_FOLD_WAIT_MS = 1500;

/** True when the floating phone sits on top of the element (the element is not part of the phone). */
export function coveredByPhone(el: Element): boolean {
  const phone = document.querySelector('[data-testid="demo-phone-floating"]');
  if (!phone || phone.contains(el) || el.contains(phone)) return false;
  if (typeof document.elementFromPoint !== "function") return false;
  const r = el.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return false;
  const hit = document.elementFromPoint(r.left + r.width / 2, r.top + r.height / 2);
  return hit !== null && phone.contains(hit);
}

/** A dialog the visitor must deal with first. A side panel, the guide card and a dialog that is closing do not count. */
const MODAL_DIALOG =
  '[role="dialog"]:not(.motion-drawer):not([data-testid="demo-guide-card"]), [role="alertdialog"]';

/**
 * A closing dialog holds the card while it is still drawn, at most this long
 * (a dialog that stays mounted never holds it for good). A slow machine can
 * take well over its nominal fade to draw it out.
 */
export const DIALOG_CLOSE_WAIT_MS = 2500;

/** True when a dialog marked closed is still drawn: it is sliding or fading out. */
function stillDrawn(el: HTMLElement): boolean {
  let style: CSSStyleDeclaration;
  try {
    style = window.getComputedStyle(el);
  } catch {
    return false;
  }
  if (style.display === "none" || style.visibility === "hidden") return false;
  const opacity = Number.parseFloat(style.opacity);
  return Number.isNaN(opacity) || opacity > 0.01;
}

/**
 * The dialog on the page, or null. An open dialog counts at once. A dialog in
 * its closing animation (data-state closed) still counts while it is drawn, so
 * the card never shows over it (#2824). Follows the page as dialogs come and go.
 */
function useOpenDialog(): HTMLElement | null {
  const closingSince = useRef(new WeakMap<Element, number>());
  const find = useCallback((): HTMLElement | null => {
    let closing: HTMLElement | null = null;
    for (const el of Array.from(document.querySelectorAll<HTMLElement>(MODAL_DIALOG))) {
      if (el.getAttribute("data-state") !== "closed") return el;
      const since = closingSince.current.get(el) ?? performance.now();
      closingSince.current.set(el, since);
      if (!closing && performance.now() - since < DIALOG_CLOSE_WAIT_MS && stillDrawn(el)) {
        closing = el;
      }
    }
    return closing;
  }, []);
  const [dialog, setDialog] = useState<HTMLElement | null>(() => find());
  useEffect(() => {
    let timer = 0;
    const read = () => {
      window.clearTimeout(timer);
      timer = 0;
      const next = find();
      setDialog((prev) => (prev === next ? prev : next));
      // A closing dialog changes nothing in the DOM when it finishes: look again.
      if (next?.getAttribute("data-state") === "closed") timer = window.setTimeout(read, 50);
    };
    read();
    const observer = new MutationObserver(read);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["data-state"],
    });
    return () => {
      window.clearTimeout(timer);
      observer.disconnect();
    };
  }, [find]);
  return dialog;
}

/** True while a side panel is on the page, open or sliding out. */
function useSidePanelOpen(): boolean {
  const [open, setOpen] = useState(() => sidePanelOnPage());
  useEffect(() => {
    const read = () => setOpen(sidePanelOnPage());
    read();
    const observer = new MutationObserver(read);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);
  return open;
}

/** The first of the named anchors that is laid out. A name can match several elements (a desktop and a phone copy). */
function firstShownAnchor(
  anchors: string[] | undefined,
  ignore?: IgnoreAnchor,
): HTMLElement | null {
  for (const name of anchors ?? []) {
    const all = document.querySelectorAll<HTMLElement>(`[data-tour="${name}"]`);
    for (const el of Array.from(all)) {
      if (ignore?.(el)) continue;
      if (isAnchorShown(el)) return el;
    }
  }
  return null;
}

/**
 * "What the solve changed" waits for the summary strip above the roster to show
 * its solved tiles before the card lands: the strip reflows when the result
 * arrives and moves the grid under the ring. Longer than this, the stop lands
 * anyway (a roster that never solves, or tiles that stay "Not solved yet").
 */
export const SUMMARY_WAIT_MAX_MS = 4000;

/** True while the roster's summary strip still loads or reads "Not solved yet". */
export function summaryStripPending(): boolean {
  const strip = document.querySelector('[data-testid="summary-strip"]');
  if (!strip) return false;
  if (strip.querySelector('[data-state="loading"]')) return true;
  return Array.from(strip.querySelectorAll('[data-testid$="-line"]')).some(
    (line) => line.textContent === "Not solved yet",
  );
}

/** A latched anchor that is gone or hidden this long is looked for again. */
const LOSS_DEBOUNCE_MS = 150;

/**
 * Presses that leave Autoplay playing: its own controls and the guide card
 * (Next, Back, or the card itself when it moved under the mouse). Skip tour
 * asks a question, so it still pauses.
 */
const AUTOPLAY_SAFE_CONTROLS = '[data-autoplay-control], [data-testid="demo-guide-card"]';
/**
 * A scenario takes clicks only on these, on the lit target, and inside the phone while it is lit.
 * The docked Solve console counts: its run button is how a visitor solves by hand (#2824).
 */
const SCENARIO_ALLOWED =
  '[data-autoplay-control], [data-testid="demo-guide-card"], [data-tour="nav-change-scenario"], [data-testid="demo-scenario-dialog"], [data-testid="cycle-solve-dock"], [role="dialog"], [role="listbox"], [role="menu"]';
const PHONE_TARGET = '[data-tour="demo-phone"], [data-testid="demo-phone-view"]';
const AUTOPLAY_PAUSING_CONTROLS = '[data-testid="demo-guide-skip"]';
/** Things a click can act on. A click on blank page or text leaves Autoplay playing. */
const ACTIONABLE =
  'a[href], button, input, select, textarea, label, summary, [contenteditable="true"], [role="button"], [role="link"], [role="tab"], [role="menuitem"], [role="option"], [role="checkbox"], [role="radio"], [role="switch"], [role="gridcell"], [tabindex]:not([tabindex="-1"])';

function isActionable(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  if (target.closest(ACTIONABLE)) return true;
  // A clickable div with no role still shows a hand cursor.
  for (let el: Element | null = target; el && el !== document.body; el = el.parentElement) {
    if (window.getComputedStyle(el).cursor === "pointer") return true;
  }
  return false;
}

/**
 * Looks for the step's anchor. `ready` once it shows or the wait is over.
 * The wait is `ANCHOR_WAIT_MS` with no data on the way (a page's rows come
 * after its fetch), and never longer than `ANCHOR_WAIT_MAX_MS`. When the wait
 * ends with no anchor, the step moves to its fallback anchors (`anchors` in
 * the result), so the card still points at something. With no fallback the
 * step shows a centred card, and stays centred: an anchor that shows later
 * does not move the card (#2824).
 *
 * Once a valid anchor is found it is latched, and the step counts as found
 * until the step or the address changes. A latched element that leaves the
 * page or hides for more than `LOSS_DEBOUNCE_MS` is looked for again; the
 * spotlight keeps the last ring meanwhile. `element()` returns the latched
 * element, for the spotlight to measure.
 */
function useStepAnchor(
  step: GuideStep | null,
  active: boolean,
  pathname: string,
  ignore: IgnoreAnchor,
) {
  // Each flag holds the step it was set for, so a new step reads false at once,
  // in the same render, and never shows the last step's result for a frame.
  const key = `${step?.id}|${pathname}`;
  const [foundKey, setFoundKey] = useState<string | null>(null);
  const [waitedKey, setWaitedKey] = useState<string | null>(null);
  const [missedKey, setMissedKey] = useState<string | null>(null);
  const [fallbackKey, setFallbackKey] = useState<string | null>(null);
  const found = foundKey === key && missedKey !== key;
  const waited = waitedKey === key;
  const onFallback = fallbackKey === key;
  const foundRef = useRef(false);
  const latchRef = useRef<HTMLElement | null>(null);
  const stepId = step?.id;
  const primary = step?.anchors;
  const preferFirst = step?.preferFirstAnchor === true;
  const fallback = step?.fallbackAnchors;
  const anchors = onFallback ? fallback : primary;
  // The step's wait began here (per step and address).
  const startRef = useRef({ key: "", at: 0 });
  if (startRef.current.key !== key) startRef.current = { key, at: performance.now() };
  const fetching = useIsFetching() > 0;
  const fetchingRef = useRef(fetching);
  fetchingRef.current = fetching;
  const [dataCapKey, setDataCapKey] = useState<string | null>(null);
  const foundNow = foundKey === key;
  useEffect(() => {
    if (!foundNow) return;
    const timer = window.setTimeout(() => setDataCapKey(key), DATA_WAIT_MAX_MS);
    return () => window.clearTimeout(timer);
  }, [foundNow, key]);
  // Ready once, ready for the whole step: a fetch later in the step never
  // takes the card back.
  const readyRef = useRef<string | null>(null);
  const readyNow =
    !primary?.length || ((found || waited) && (!fetching || dataCapKey === key || waited));
  if (readyNow) readyRef.current = key;
  // biome-ignore lint/correctness/useExhaustiveDependencies: restart per step
  useEffect(() => {
    foundRef.current = false;
    latchRef.current = null;
  }, [stepId, active, pathname]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: restart per step, and when data starts or stops loading
  useEffect(() => {
    if (!active || !primary?.length || waitedKey === key) return;
    const spent = performance.now() - startRef.current.at;
    const left = Math.max(
      0,
      fetching ? ANCHOR_WAIT_MAX_MS - spent : Math.min(ANCHOR_WAIT_MS, ANCHOR_WAIT_MAX_MS - spent),
    );
    const timer = window.setTimeout(() => {
      setWaitedKey(key);
      if (foundRef.current) return;
      // A later anchor held back for the first one (#2962): the wait is over, light it.
      const late = preferFirst ? firstShownAnchor(primary, ignore) : null;
      if (late) {
        latchRef.current = late;
        foundRef.current = true;
        setFoundKey(key);
        return;
      }
      if (fallback?.length) setFallbackKey(key);
      else setMissedKey(key);
    }, left);
    return () => window.clearTimeout(timer);
  }, [stepId, active, pathname, fetching, waitedKey]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: restart per step and per anchor set
  useEffect(() => {
    if (!active || !anchors?.length) return;
    let raf = 0;
    let poll = 0;
    let pollCap = 0;
    let lostTimer = 0;
    const stopPoll = () => {
      window.clearInterval(poll);
      window.clearTimeout(pollCap);
      poll = 0;
      pollCap = 0;
    };
    // The step prefers its first anchor (Publish before the More menu, #2962):
    // a later one is not lit while the first may still come, within the
    // anchor wait, so the ring never jumps from More to Publish in the step.
    const holdForFirst = () =>
      holdsForFirstAnchor(
        preferFirst,
        onFallback,
        anchors.length,
        performance.now() - startRef.current.at,
        fetchingRef.current,
      );
    const search = () => {
      const el = firstShownAnchor(holdForFirst() ? anchors.slice(0, 1) : anchors, ignore);
      if (!el) return;
      latchRef.current = el;
      foundRef.current = true;
      setFoundKey(key);
      stopPoll();
    };
    const check = () => {
      raf = 0;
      const latched = latchRef.current;
      if (!latched) {
        search();
        return;
      }
      if (onFallback) {
        // The step fell back while its own anchor was still on the way (the ringed cells
        // appear after the Solve is recorded): move to it as soon as it shows (#2824).
        const own = firstShownAnchor(primary, ignore);
        if (own && own !== latched) {
          latchRef.current = own;
          setFoundKey(key);
          return;
        }
      }
      if (preferFirst && anchors.length > 1) {
        // The step's first anchor came after a later one was lit: light the first.
        const best = firstShownAnchor(anchors.slice(0, 1), ignore);
        if (best && best !== latched) {
          latchRef.current = best;
          setFoundKey(key);
          return;
        }
      }
      // The anchor name moved to another element (the demo change's next
      // control, #2824): light the new one.
      const named = anchors.includes(latched.getAttribute("data-tour") ?? "");
      if (!named) {
        const moved = firstShownAnchor(anchors, ignore);
        if (moved) {
          latchRef.current = moved;
          const r = moved.getBoundingClientRect();
          if ((r.top < 0 || r.bottom > window.innerHeight) && moved.scrollIntoView) {
            // Instant: the card is placed against where the element ends up.
            moved.scrollIntoView({ block: "center", inline: "nearest", behavior: "instant" });
          }
          setFoundKey(key);
          return;
        }
      }
      if (named && latched.isConnected && isAnchorShown(latched)) {
        window.clearTimeout(lostTimer);
        lostTimer = 0;
        return;
      }
      if (lostTimer) return;
      // Gone or hidden: look again only if it stays so for a moment.
      lostTimer = window.setTimeout(() => {
        lostTimer = 0;
        const now = latchRef.current;
        if (now?.isConnected && isAnchorShown(now)) return;
        latchRef.current = null;
        startPoll();
        check();
      }, LOSS_DEBOUNCE_MS);
    };
    const schedule = () => {
      if (!raf) raf = window.requestAnimationFrame(check);
    };
    // Until the anchor is found, look every 100 ms (it may only get its size,
    // or lose its old-page mark, without a page change). Frames stop in a
    // hidden tab, so the poll checks directly.
    function startPoll() {
      stopPoll();
      poll = window.setInterval(check, 100);
      const spent = performance.now() - startRef.current.at;
      pollCap = window.setTimeout(stopPoll, Math.max(ANCHOR_WAIT_MS, ANCHOR_WAIT_MAX_MS - spent));
    }
    const observer = new MutationObserver(schedule);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["data-tour", "class", "style", "hidden"],
    });
    if (!latchRef.current) startPoll();
    check();
    // The hold for the first anchor ends: look again at once (the poll may have stopped).
    const holdEnds = holdForFirst()
      ? [ANCHOR_WAIT_MS, ANCHOR_WAIT_MAX_MS].map((ms) =>
          window.setTimeout(check, ms - (performance.now() - startRef.current.at) + 20),
        )
      : [];
    return () => {
      for (const t of holdEnds) window.clearTimeout(t);
      observer.disconnect();
      stopPoll();
      window.cancelAnimationFrame(raf);
      window.clearTimeout(lostTimer);
    };
  }, [stepId, active, onFallback, pathname, ignore, preferFirst]);
  const hasAnchors = Boolean(primary?.length);
  const element = useCallback((): HTMLElement | null => {
    const el = latchRef.current;
    return el?.isConnected ? el : null;
  }, []);
  return {
    anchors,
    element,
    found: hasAnchors && found,
    ready: readyRef.current === key,
  };
}

type AutoplayState = "off" | "playing" | "paused";

/** Autoplay's mode and the tour it played last, kept across a remount. */
let autoplayMode: AutoplayState = "off";
let autoplayPlayed: string | null = null;
/**
 * The visitor wants Autoplay: true from Autoplay or Resume until Pause, Stop,
 * Skip or Leave scenario. A scenario that ends, or a stray click, leaves it
 * true, so the next scenario plays by itself (#2824).
 */
let autoplayWanted = false;

/**
 * True while a step that prefers its first anchor (Publish before the More
 * menu, #2962) does not light a later one yet: the first may still come within
 * the anchor wait, or while the page still loads data. A step on its fallback
 * anchors never holds.
 */
export function holdsForFirstAnchor(
  preferFirst: boolean,
  onFallback: boolean,
  anchorCount: number,
  elapsedMs: number,
  fetching = false,
): boolean {
  if (!preferFirst || onFallback || anchorCount < 2) return false;
  // Data on the way (the roster after a solve brings the Publish button): wait
  // for it, never longer than the longest anchor wait.
  return elapsedMs < (fetching ? ANCHOR_WAIT_MAX_MS : ANCHOR_WAIT_MS);
}

/** Tests only: forget Autoplay's mode between tests (`played`: the tour played last). */
export function resetAutoplayForTests(played: string | null = null): void {
  autoplayMode = "off";
  autoplayPlayed = played;
  autoplayWanted = false;
  resetPointerForTests();
}

function GuideRuntime({
  token,
  teamId,
  cycleId,
  chapters,
  toMake,
}: {
  token: string;
  teamId: string;
  cycleId: string | null;
  chapters: ChapterMap;
  /** What the visitor still has to change (the exact values for "Make the change"). */
  toMake?: DemoChangeToMake | null | undefined;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const makeDetail = makeChangeDetail(toMake);
  const ctx = useMemo<GuideContext>(() => ({ teamId, cycleId }), [teamId, cycleId]);
  const [state, setState] = useState<GuideState>(() => loadGuideState(token, chapters));
  const stateRef = useRef(state);
  const [chooser, setChooser] = useState(false);
  // Phone first: every start of a guide (chooser, Autoplay, scenario, a restart) checks the phone once.
  const [gateNonce, setGateNonce] = useState(0);
  const bumpGate = useCallback(() => setGateNonce((n) => n + 1), []);
  // #2824 - the "Pick a change" step: applying a change moves the guide on.
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;
  const searchRef = useRef(location.search);
  searchRef.current = location.search;
  const prevPathRef = useRef(location.pathname);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  // #2824 - the page swap can lag behind the address. When the guide or a
  // link is about to move the address, the anchor elements on the page are
  // noted once. After the move they are the old page's and are not lit while
  // the new page loads. Nothing scans the page between moves.
  const snapshotRef = useRef<{ elements: Set<Element>; at: number } | null>(null);
  const staleRef = useRef<StaleAnchors>({ elements: new Set(), since: 0 });
  const ignore = useMemo(() => staleIgnore(staleRef.current), []);
  const noteOldPage = useCallback(() => {
    snapshotRef.current = {
      elements: new Set(document.querySelectorAll("[data-tour]")),
      at: performance.now(),
    };
  }, []);
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (e.target instanceof Element && e.target.closest("a[href]")) noteOldPage();
    };
    document.addEventListener("click", onClick, true);
    window.addEventListener("popstate", noteOldPage, true);
    return () => {
      document.removeEventListener("click", onClick, true);
      window.removeEventListener("popstate", noteOldPage, true);
    };
  }, [noteOldPage]);
  // A step opened on another page waits for the address and the page to move.
  const [pendingNav, setPendingNav] = useState(false);
  const lastKeyRef = useRef(location.pathname);
  useLayoutEffect(() => {
    if (lastKeyRef.current === location.pathname) return;
    lastKeyRef.current = location.pathname;
    const noted = snapshotRef.current;
    snapshotRef.current = null;
    const now = performance.now();
    staleRef.current.elements = noted && now - noted.at < 3000 ? noted.elements : new Set();
    staleRef.current.since = now;
  }, [location.pathname]);

  /** Set the new state. `drive`: also open the page the new step lives on. */
  const commit = useCallback(
    (next: GuideState, drive = false) => {
      stateRef.current = next;
      setState(next);
      saveGuideState(token, next);
      if (!drive) return;
      const step = currentStep(next, chapters, activeTourId(next, chapters, pathRef.current));
      if (step && !step.route(pathRef.current, searchRef.current) && step.openPath) {
        const target = step.openPath(ctx);
        if (target !== pathRef.current + searchRef.current) {
          noteOldPage();
          setPendingNav(true);
        }
        navigate(target);
      }
    },
    [token, navigate, ctx, chapters, noteOldPage],
  );

  // Help → "Show the guide".
  useEffect(
    () =>
      subscribeDemoGuideChooser(() => {
        bumpGate();
        setChooser(true);
      }),
    [bumpGate],
  );

  // Autoplay presses on arrival (#2962), so a step's action can finish before
  // its bar ends. The step then moves on as its bar ends, never before. The
  // bar of the step on show, and the move that waits for its end.
  const barEndRef = useRef<{ key: string; at: number } | null>(null);
  const pendingAdvanceRef = useRef<{ key: string; run: () => void } | null>(null);
  // The step whose action finished while its bar still ran (the bar does not wait at its end).
  const [advanceHeld, setAdvanceHeld] = useState<string | null>(null);
  const holdAdvance = useCallback((key: string, run: () => void) => {
    const bar = barEndRef.current;
    if (autoplayMode !== "off" && bar?.key === key && Date.now() < bar.at) {
      pendingAdvanceRef.current = { key, run };
      setAdvanceHeld(key);
      return;
    }
    run();
  }, []);

  // A step that waits for the visitor to do something (solve, publish, send or
  // accept a request): the page or the phone reports it. A scenario tour then
  // opens the page of its next step.
  useEffect(
    () =>
      subscribeDemoGuideAction((action) => {
        const s = stateRef.current;
        const tour = activeTourId(s, chapters, pathRef.current);
        const now = currentStep(s, chapters, tour);
        if (tour && now?.done.type === "action" && now.done.action === action) {
          const key = `${tour}:${placeOf(s, tour).stepIndex}`;
          holdAdvance(key, () => {
            const latest = stateRef.current;
            if (`${tour}:${placeOf(latest, tour).stepIndex}` !== key) return;
            commit(goNext(latest, chapters, tour), chapters[tour]?.kind === "scenario");
          });
        }
      }),
    [chapters, commit, holdAdvance],
  );

  // "Use this change" was pressed and the team is being rebuilt: the old card
  // goes and the phone folds at once, before the server answers (#2824).
  const [changing, setChanging] = useState(false);
  useEffect(
    () =>
      subscribeDemoGuideScenarioPending((pending) => {
        setChanging(pending);
        if (pending) {
          setChooser(false);
          closeDemoPhone();
        }
      }),
    [],
  );
  useEffect(() => {
    if (!changing) return;
    // The server never answered: do not leave the guide hidden for good.
    const timer = window.setTimeout(() => setChanging(false), CHANGE_WAIT_MS);
    return () => window.clearTimeout(timer);
  }, [changing]);

  // Autoplay: the guide plays a flow by itself (#2824).
  // Kept outside React too: the guide remounts when the shell does (a page
  // in another layout, a sign-in refresh), and Autoplay must play on.
  const [autoplay, setAutoplayState] = useState<AutoplayState>(() => autoplayMode);
  const autoplayRef = useRef<AutoplayState>(autoplayMode);
  // The tour Autoplay played last, so the introduction can play on.
  const playedRef = useRef<string | null>(autoplayPlayed);
  const setAutoplay = useCallback((next: AutoplayState, chosen = false) => {
    if (next === "playing") autoplayWanted = true;
    else if (chosen) autoplayWanted = false;
    autoplayMode = next;
    autoplayRef.current = next;
    setAutoplayState(next);
  }, []);
  // The visitor picked a scenario: its tour starts over and the phone switches
  // to the person it is about.
  useEffect(
    () =>
      subscribeDemoGuideScenario((variant) => {
        const chapter = chapters[scenarioTourId(variant)];
        if (!chapter) return;
        bumpGate();
        setChooser(false);
        setWaiting(false);
        setChanging(false);
        commit(startScenario(stateRef.current, variant));
        // Autoplay was on before this change: the new scenario plays too.
        if (autoplayWanted && autoplayRef.current !== "playing") setAutoplay("playing");
        // The scenario takes over: its first page opens now, so no card shows
        // on the roster the change just opened. (Applying a change always opens
        // the roster; the router has not rendered it yet, so it is named here.)
        const first = chapter.steps[0];
        if (first?.openPath && !first.route(`/teams/${ctx.teamId}/roster`, "")) {
          noteOldPage();
          setPendingNav(true);
          navigate(first.openPath(ctx));
        }
        notifyDemoPhoneScenarioStart();
        // The phone learns the person. It opens only when the first step is about the phone.
        if (chapter.memberId) {
          showDemoPhonePerson(chapter.memberId, { open: first ? stepNeedsPhone(first) : false });
        }
      }),
    [chapters, commit, bumpGate, noteOldPage, navigate, ctx, setAutoplay],
  );

  const actionTimerRef = useRef(0);
  // The pointer shows while Autoplay plays, and hides on Pause and Stop (#2962).
  useEffect(() => {
    setPointerMode(autoplay);
  }, [autoplay]);
  // Autoplay stopped: a move that waited for the bar's end goes now.
  useEffect(() => {
    if (autoplay !== "off") return;
    const pending = pendingAdvanceRef.current;
    pendingAdvanceRef.current = null;
    barEndRef.current = null;
    pending?.run();
  }, [autoplay]);
  const pauseLeftRef = useRef<{ key: string; ms: number } | null>(null);
  useEffect(
    () =>
      subscribeDemoGuideAutoplay((target: AutoplayTarget) => {
        const s = stateRef.current;
        const restart = (id: string): GuideState => ({
          ...s,
          tours: { ...s.tours, [id]: { stepIndex: 0, status: "open" } },
        });
        bumpGate();
        setChooser(false);
        setWaiting(false);
        let id: string | null = INTRO_ID;
        if (target.kind === "intro") {
          commit(restart(INTRO_ID), true);
        } else {
          id =
            target.kind === "page"
              ? pageTourId(chapters, pathRef.current)
              : activeTourId(s, chapters, pathRef.current);
          if (!id) return;
          commit(target.kind === "page" ? restart(id) : s, true);
        }
        // The chosen tour is the one Autoplay plays now (#2962): a scenario
        // played before must not read this page tour as "the next one" and
        // turn Autoplay off as it opens.
        playedRef.current = id;
        autoplayPlayed = id;
        setAutoplay("playing");
      }),
    [chapters, commit, setAutoplay, bumpGate],
  );
  // A real click by the visitor pauses Autoplay. The guide's own presses are marked.
  // Next and Back only move the tour: Autoplay plays on from the new step.
  // The press counts where it began: Autoplay can move the card between the
  // mouse going down and coming up, and the click then lands on a parent.
  useEffect(() => {
    if (autoplay !== "playing") return;
    let downOnControl = false;
    const onControl = (target: EventTarget | null) =>
      target instanceof Element &&
      target.closest(AUTOPLAY_SAFE_CONTROLS) !== null &&
      target.closest(AUTOPLAY_PAUSING_CONTROLS) === null;
    const onDown = (e: PointerEvent) => {
      downOnControl = onControl(e.target);
    };
    const onClick = (e: MouseEvent) => {
      const fromControl = downOnControl;
      downOnControl = false;
      if (isAutoplayClick() || fromControl || onControl(e.target)) return;
      // A click on blank page or plain text does nothing: Autoplay plays on.
      if (!isActionable(e.target)) return;
      // The scenario lock may block this click (its listener can run after
      // this one): a blocked click did nothing either.
      window.setTimeout(() => {
        if (!e.defaultPrevented) setAutoplay("paused");
      }, 0);
    };
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("click", onClick, true);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("click", onClick, true);
    };
  }, [autoplay, setAutoplay]);

  // The visitor pressed the highlighted link: the address moved on.
  useEffect(() => {
    const prev = prevPathRef.current;
    prevPathRef.current = location.pathname;
    if (prev === location.pathname) return;
    const s = stateRef.current;
    const tour = activeTourId(s, chapters, prev);
    const step = currentStep(s, chapters, tour);
    if (
      tour &&
      step?.done.type === "route" &&
      step.route(prev) &&
      step.done.to(location.pathname)
    ) {
      // Autoplay pressed the link on arrival: the step moves on as its bar ends (#2962).
      const key = `${tour}:${placeOf(s, tour).stepIndex}`;
      holdAdvance(key, () => {
        const latest = stateRef.current;
        if (`${tour}:${placeOf(latest, tour).stepIndex}` !== key) return;
        commit(goNext(latest, chapters, tour));
      });
    }
  }, [location.pathname, commit, chapters, holdAdvance]);

  // The introduction ends alone (#2962): the page tour of the page the visitor
  // stands on does not start on top of its last card. It starts on their next
  // visit to that page, or from Help.
  const introOpenNow = placeOf(state, INTRO_ID).status === "open";
  const introWasOpenRef = useRef(introOpenNow);
  const heldPageTourRef = useRef<{ id: string; path: string } | null>(null);
  if (introWasOpenRef.current && !introOpenNow) {
    const id = pageTourId(chapters, location.pathname);
    heldPageTourRef.current = id ? { id, path: location.pathname } : null;
  }
  introWasOpenRef.current = introOpenNow;
  if (heldPageTourRef.current && heldPageTourRef.current.path !== location.pathname) {
    heldPageTourRef.current = null;
  }
  const rawTourId = activeTourId(state, chapters, location.pathname);
  const tourId = rawTourId && rawTourId === heldPageTourRef.current?.id ? null : rawTourId;
  const step = currentStep(state, chapters, tourId);
  const visible = chooser || step !== null;
  const routeOk = step ? step.route(location.pathname, location.search) : true;
  const placeKey = `${tourId}:${tourId ? placeOf(state, tourId).stepIndex : -1}`;
  // Autoplay pressed for this step (#2962): its card stays, with its bar, until
  // the bar ends. The page may change under it meanwhile (a link opened a new
  // page, a confirm dialog opened, the lit control went away).
  const [heldPlace, setHeldPlace] = useState<string | null>(null);
  const held = autoplay !== "off" && heldPlace === placeKey;
  const heldRef = useRef(held);
  heldRef.current = held;
  const routeOkShown = routeOk || held;
  const anchor = useStepAnchor(step, visible && !chooser && routeOk, location.pathname, ignore);
  // Phone first: a guide that starts, or a step that is not about the phone,
  // folds an open phone before its card or highlight shows. The check runs once
  // per start and step: a phone the visitor opens later is left alone.
  const phoneOpen = useSyncExternalStore(subscribeDemoPhoneOpen, isDemoPhoneOpen, () => false);
  const gateKey = chooser
    ? `chooser:${gateNonce}`
    : step && tourId
      ? `${tourId}:${step.id}:${gateNonce}`
      : null;
  const [clearedKey, setClearedKey] = useState<string | null>(null);
  const phoneUnwanted = chooser || (step !== null && !stepNeedsPhone(step));
  const phoneGate = gateKey !== null && phoneUnwanted && phoneOpen && clearedKey !== gateKey;
  useEffect(() => {
    if (gateKey === null || clearedKey === gateKey) return;
    if (!phoneGate) {
      // The phone is folded at the start: the check is done.
      if (!phoneOpen) setClearedKey(gateKey);
      return;
    }
    closeDemoPhone();
    const timer = window.setTimeout(() => setClearedKey(gateKey), PHONE_FOLD_WAIT_MS);
    return () => window.clearTimeout(timer);
  }, [gateKey, phoneGate, phoneOpen, clearedKey]);
  // A lit element under the open phone: fold the phone, and never light it meanwhile.
  const litEl = step && routeOk && anchor.found ? anchor.element() : null;
  const underPhone = !held && phoneOpen && litEl !== null && coveredByPhone(litEl);
  useEffect(() => {
    if (underPhone) closeDemoPhone();
  }, [underPhone]);
  // A dialog open over the page (Change the scenario) is never covered by the card.
  const openDialog = useOpenDialog();
  const dialogBlocks = !held && openDialog !== null && !(litEl && openDialog.contains(litEl));
  // A side panel still open (or sliding out) is never covered by the card of a
  // step about the phone: the phone opens over that edge once the panel is gone.
  const drawerOpen = useSidePanelOpen();
  // The page the step lives on has arrived (or the wait is over).
  useEffect(() => {
    if (!pendingNav) return;
    if (routeOk) {
      setPendingNav(false);
      return;
    }
    const timer = window.setTimeout(() => setPendingNav(false), NAV_WAIT_MS);
    return () => window.clearTimeout(timer);
  }, [pendingNav, routeOk]);

  // Focus returns to where it was when the guide closes.
  useLayoutEffect(() => {
    if (visible) {
      if (!returnFocusRef.current && document.activeElement instanceof HTMLElement) {
        returnFocusRef.current = document.activeElement;
      }
      return;
    }
    const back = returnFocusRef.current;
    returnFocusRef.current = null;
    if (back?.isConnected) back.focus();
  }, [visible]);

  const latchedElement = anchor.element;
  const getElement = useCallback(
    () => (step && routeOk ? latchedElement() : null),
    [step, routeOk, latchedElement],
  );

  const closeGuide = useCallback(() => {
    setAutoplay("off", true);
    if (tourId) commit(skip(stateRef.current, tourId));
  }, [commit, tourId, setAutoplay]);

  // Skip tour asks once before it closes the guide. A new step clears it.
  const [confirmSkip, setConfirmSkip] = useState(false);
  // A scenario plays (#2824): the phone stays on its one person and the visitor
  // can only follow the walkthrough. Skip does nothing. A stray click or Escape
  // asks first: Stay keeps the tour, Leave scenario ends it and replays the click.
  const inScenario = tourId?.startsWith("scenario:") === true;
  const [confirmLeave, setConfirmLeave] = useState(false);
  const heldClickRef = useRef<Element | null>(null);
  const resumeAfterStayRef = useRef(false);
  const replayingRef = useRef(false);
  const scenarioChapter = tourId ? chapters[tourId] : undefined;
  const lockedMemberId = scenarioChapter?.memberId ?? scenarioChapter?.phoneMemberId ?? null;
  useEffect(() => {
    if (!inScenario) return;
    setDemoPhoneScenarioLock({ memberId: lockedMemberId });
    return () => setDemoPhoneScenarioLock(null);
  }, [inScenario, lockedMemberId]);
  const getElementRef = useRef<() => HTMLElement | null>(() => null);
  getElementRef.current = getElement;
  useEffect(() => {
    if (!inScenario) return;
    const inPhoneTarget = () => {
      const target = getElementRef.current();
      return Boolean(target?.closest(PHONE_TARGET) || target?.querySelector(PHONE_TARGET));
    };
    const onClick = (e: MouseEvent) => {
      if (isAutoplayClick() || replayingRef.current) return;
      const t = e.target;
      if (t instanceof Element) {
        if (t.closest(SCENARIO_ALLOWED)) return;
        const target = getElementRef.current();
        if (target?.contains(t)) return;
      }
      e.preventDefault();
      e.stopPropagation();
      // Hold the click and ask: the visitor decides whether to leave.
      heldClickRef.current = t instanceof Element ? t : null;
      if (autoplayRef.current === "playing") {
        resumeAfterStayRef.current = true;
        setAutoplay("paused", true);
      }
      setConfirmLeave(true);
    };
    document.addEventListener("click", onClick, true);
    // The phone is a frame: its clicks never reach this page. Open only while
    // the lit area is the phone itself.
    const seen = new WeakSet<Document>();
    const guard = (e: MouseEvent) => {
      if (isAutoplayClick() || inPhoneTarget()) return;
      e.preventDefault();
      e.stopPropagation();
    };
    const attach = () => {
      for (const f of Array.from(
        document.querySelectorAll<HTMLIFrameElement>('iframe[title="Member app"]'),
      )) {
        let doc: Document | null = null;
        try {
          doc = f.contentDocument;
        } catch {
          doc = null;
        }
        if (!doc || seen.has(doc)) continue;
        seen.add(doc);
        doc.addEventListener("click", guard, true);
      }
    };
    attach();
    const timer = window.setInterval(attach, 200);
    return () => {
      document.removeEventListener("click", onClick, true);
      window.clearInterval(timer);
      for (const f of Array.from(
        document.querySelectorAll<HTMLIFrameElement>('iframe[title="Member app"]'),
      )) {
        try {
          f.contentDocument?.removeEventListener("click", guard, true);
        } catch {
          // A frame we cannot read has no listener from us.
        }
      }
    };
  }, [inScenario, setAutoplay]);
  const leaveScenario = useCallback(() => {
    setAutoplay("off", true);
    const s = stateRef.current;
    commit({ ...(tourId ? skip(s, tourId) : s), activeScenario: null });
  }, [commit, tourId, setAutoplay]);
  const askLeave = useCallback(() => {
    if (autoplayRef.current === "playing") {
      resumeAfterStayRef.current = true;
      setAutoplay("paused", true);
    }
    setConfirmLeave(true);
  }, [setAutoplay]);
  const stayInScenario = () => {
    heldClickRef.current = null;
    setConfirmLeave(false);
    if (resumeAfterStayRef.current) setAutoplay("playing");
    resumeAfterStayRef.current = false;
  };
  const leaveAndReplay = () => {
    const held = heldClickRef.current;
    heldClickRef.current = null;
    resumeAfterStayRef.current = false;
    setConfirmLeave(false);
    leaveScenario();
    if (!held) return;
    // Once the scenario state is cleared, the held click goes through as it was meant.
    window.setTimeout(() => {
      if (!held.isConnected) return;
      replayingRef.current = true;
      try {
        (
          (held.closest("a, button, [role=button]") as HTMLElement | null) ?? (held as HTMLElement)
        ).click();
      } finally {
        replayingRef.current = false;
      }
    }, 120);
  };
  // A new scenario step, or no scenario, clears the question.
  // biome-ignore lint/correctness/useExhaustiveDependencies: clear when the tour changes
  useEffect(() => {
    setConfirmLeave(false);
    heldClickRef.current = null;
  }, [tourId, inScenario]);
  // An action step steps aside once its element is pressed, so the page (a
  // confirm dialog, the solve) is free to use. A new step brings it back.
  const [waiting, setWaiting] = useState(false);
  // The visitor works on the phone for "request-sent": the card stays.
  const waitsForAction =
    step?.done.type === "action" && (step.done.stepAside ?? step.done.action !== "request-sent");
  useEffect(() => {
    if (!waitsForAction) return;
    const onClick = (e: MouseEvent) => {
      const el = getElement();
      if (el && e.target instanceof Node && el.contains(e.target)) setWaiting(true);
    };
    // A lit menu trigger opens on pointerdown, and its menu takes the click
    // (#2824: People's More): the card steps aside on the press itself.
    const onDown = (e: PointerEvent) => {
      const el = getElement();
      if (el?.getAttribute("aria-haspopup") !== "menu") return;
      if (e.target instanceof Node && el.contains(e.target)) setWaiting(true);
    };
    document.addEventListener("click", onClick, true);
    document.addEventListener("pointerdown", onDown, true);
    return () => {
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("pointerdown", onDown, true);
    };
  }, [waitsForAction, getElement]);
  // Each visit to a step has its own number: Back and Next to the same step is a new visit.
  const visitRef = useRef({ key: "", n: 0 });
  if (visitRef.current.key !== placeKey) {
    visitRef.current = { key: placeKey, n: visitRef.current.n + 1 };
  }
  const visit = visitRef.current.n;
  // The visit whose card has landed (ring and card on screen), and its lit element.
  const [shownVisit, setShownVisit] = useState(-1);
  const shownElRef = useRef<{ visit: number; el: HTMLElement | null }>({ visit: -1, el: null });
  const [showGaveUp, setShowGaveUp] = useState(-1);
  const frameStepKey = step && tourId ? `${tourId}:${step.id}` : null;
  const stepMember = tourId ? (chapters[tourId]?.memberId ?? null) : null;
  // The change to make, for the pointer and the press of "Make the change" (#2962).
  const toMakeRef = useRef(toMake ?? null);
  toMakeRef.current = toMake ?? null;
  // The bar's length on this step, for the pointer's glide (#2962).
  const barMsRef = useRef(AUTOPLAY_STEP_MS);
  // The pointer goes to the step's press target from the landing frame on,
  // read every frame until it shows (#2962); else to the lit element.
  const followPointer = useCallback(
    (s: GuideStep, el: HTMLElement | null) => {
      followStep(pointerTarget(s, el, stepMember, toMakeRef.current), true, {
        // A step that only reads has no press: the pointer goes to the ring at once.
        press:
          s.done.type === "route" || s.done.type === "action"
            ? () => pressTargetOf(s, el, stepMember, toMakeRef.current)
            : undefined,
      });
    },
    [stepMember],
  );
  // The ring moved after its card landed (the page reflowed): the pointer follows.
  const onRingMove = useCallback(
    (key: string, el: HTMLElement | null) => {
      if (key !== frameStepKey || !step) return;
      shownElRef.current = { visit: visitRef.current.n, el };
      // After Autoplay pressed, the presses move the pointer (#2962).
      if (autoplayRef.current === "playing" && !heldRef.current) followPointer(step, el);
    },
    [frameStepKey, step, followPointer],
  );
  // "What the solve changed" waits for the summary strip to be solved (see above).
  // The roster tour's last step (#2962): the scenario picker opening, by
  // Autoplay, by the card's button or by the visitor's own press, ends it.
  const opensScenarios =
    step?.done.type === "action" && step.done.action === "scenario-opened" && routeOk;
  useEffect(() => {
    if (!opensScenarios) return;
    let sent = false;
    const look = () => {
      if (sent || !scenarioDialogOpen()) return;
      sent = true;
      notifyDemoGuideAction("scenario-opened");
    };
    look();
    const timer = window.setInterval(look, 100);
    return () => window.clearInterval(timer);
  }, [opensScenarios]);
  const waitsForSummary = step?.liveBody === "solve-changes" && routeOk && visible && !chooser;
  const [summaryPending, setSummaryPending] = useState(false);
  const [summaryOver, setSummaryOver] = useState(-1);
  useEffect(() => {
    if (!waitsForSummary) {
      setSummaryPending(false);
      return;
    }
    const n = visit;
    const started = performance.now();
    const read = () => {
      const over = performance.now() - started >= SUMMARY_WAIT_MAX_MS;
      if (over) setSummaryOver(n);
      setSummaryPending(!over && summaryStripPending());
    };
    read();
    const timer = window.setInterval(read, 50);
    return () => window.clearInterval(timer);
  }, [waitsForSummary, visit]);
  const summaryHold = waitsForSummary && summaryPending && summaryOver !== visit;
  const onShown = useCallback(
    (key: string, el: HTMLElement | null) => {
      if (key !== frameStepKey || !step) return;
      const n = visitRef.current.n;
      shownElRef.current = { visit: n, el };
      setShownVisit(n);
      // The pointer moves with the ring, in the frame they show (#2962). The
      // bar starts in this frame: the glide fits inside it.
      if (autoplayRef.current === "playing") {
        setBarEndsAt(Date.now() + barMsRef.current);
        followPointer(step, el);
      }
    },
    [frameStepKey, step, followPointer],
  );
  // The card keeps clear of a side drawer behind the lit element (#2824).
  const avoidDrawer = useCallback(() => drawerToAvoid(getElement()), [getElement]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: clear on every new step
  useEffect(() => {
    setConfirmSkip(false);
    setWaiting(false);
    setHeldPlace(null);
    setAdvanceHeld(null);
  }, [placeKey]);

  // A scenario played by Autoplay ends with its own last step: the page tour
  // that may open next is not played.
  useEffect(() => {
    const last = playedRef.current;
    if (autoplayRef.current === "off" || !last?.startsWith("scenario:")) return;
    if (tourId && tourId !== last && !tourId.startsWith("scenario:")) setAutoplay("off");
  }, [tourId, setAutoplay]);
  useEffect(() => {
    if (tourId) {
      playedRef.current = tourId;
      autoplayPlayed = tourId;
    }
  }, [tourId]);
  // Autoplay ends when no tour is left to play. The introduction ends alone:
  // the roster tour starts when the visitor opens the roster page (#2962).
  useEffect(() => {
    if (autoplay === "off" || tourId || chooser) return;
    setAutoplay("off");
  }, [autoplay, tourId, chooser, setAutoplay]);
  // A pending give-up timer belongs to one step.
  // biome-ignore lint/correctness/useExhaustiveDependencies: clear on every new step
  useEffect(() => {
    window.clearTimeout(actionTimerRef.current);
  }, [placeKey, autoplay]);

  // The progress line: one duration for every step (#2962). It is the time
  // until Autoplay presses, or moves on.
  const pauseShownMs = autoplayPauseMs(
    step ?? undefined,
    prefersReducedMotion(),
    tourId === INTRO_ID,
  );
  barMsRef.current = pauseLeftRef.current?.key.startsWith(`${placeKey}|`)
    ? pauseLeftRef.current.ms
    : pauseShownMs;
  // Autoplay: after a readable pause, do what the visitor would do on this step.
  const stepReady =
    held ||
    (Boolean(step) &&
      !chooser &&
      !phoneGate &&
      !changing &&
      !(pendingNav && !routeOk) &&
      (!routeOk || (anchor.ready && (shownVisit === visit || showGaveUp === visit))));
  // The visit Autoplay pressed for (#2962): a resumed step never presses twice.
  const pressedRef = useRef("");
  // A card that never lands still lets Autoplay play on, later.
  const waitsForCard =
    autoplay === "playing" &&
    Boolean(step) &&
    routeOk &&
    anchor.ready &&
    !chooser &&
    !changing &&
    shownVisit !== visit &&
    showGaveUp !== visit;
  useEffect(() => {
    if (!waitsForCard) return;
    const n = visit;
    const timer = window.setTimeout(() => setShowGaveUp(n), SHOW_GIVE_UP_MS);
    return () => window.clearTimeout(timer);
  }, [waitsForCard, visit]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: one timer per step
  useEffect(() => {
    if (autoplay !== "playing" || !step || !tourId || !stepReady || confirmSkip) return;
    const reduced = prefersReducedMotion();
    const needsPage = !routeOkShown && Boolean(step.openPath);
    const full = needsPage ? OPEN_PAUSE_MS : autoplayPauseMs(step, reduced, tourId === INTRO_ID);
    // A resumed step waits only what was left when it paused, as its line shows.
    const pauseKey = `${placeKey}|${needsPage}`;
    const left = pauseLeftRef.current;
    const barLeft = left && left.key === pauseKey ? left.ms : full;
    const startedAt = Date.now();
    // When the bar started, counting the part before a pause.
    const barStart = startedAt - (full - barLeft);
    const barEnd = startedAt + barLeft;
    if (!needsPage) {
      barEndRef.current = { key: placeKey, at: barEnd };
      setBarEndsAt(barEnd, barStart);
    }
    const pressKey = `${placeKey}#${visit}`;
    const next = () => commit(goNext(stateRef.current, chapters, tourId), true);
    // A give-up waits longer by the time the pointer spent moving (#2962).
    const giveUpAfter = (ms: number, fn: () => void) => {
      const busyBefore = pointerBusyMs();
      actionTimerRef.current = window.setTimeout(() => {
        if (autoplayRef.current !== "playing") return;
        const extra = pointerBusyMs() - busyBefore;
        if (pointerBusy()) giveUpAfter(Math.max(500, extra), fn);
        else if (extra > 50) giveUpAfter(extra, fn);
        else fn();
      }, ms);
    };
    const done = step.done;
    const memberId = chapters[tourId]?.memberId ?? null;
    /**
     * Do the step's action (#2962): press its link or its control. The
     * follow-on presses (the confirm, the phone's next screens) go back to
     * back after it. The step moves on as its bar ends, or once the action
     * finishes when that is later.
     */
    const pressNow = (): boolean => {
      if (pressedRef.current === pressKey || autoplayRef.current !== "playing") return false;
      if (!stepPressesOnArrival(step)) return false;
      pressedRef.current = pressKey;
      setHeldPlace(placeKey);
      if (done.type === "route") {
        const el = firstShownAnchor(step.anchors);
        if (el) {
          el.focus();
          // Press the link as the visitor would; the address then moves on.
          // A marked press, so the pause-on-click listener lets it through.
          // A tile anchor holds its link inside it.
          const from = `${window.location.pathname}${window.location.search}`;
          void pressAndWait(el.closest("a") ?? el.querySelector("a") ?? el).then((pressed) => {
            if (!pressed && autoplayRef.current !== "playing") return;
            // A press that does not move the address: open the page directly.
            actionTimerRef.current = window.setTimeout(() => {
              if (autoplayRef.current !== "playing") return;
              if (`${window.location.pathname}${window.location.search}` === from) {
                navigate(done.go(ctx));
              }
            }, ROUTE_GIVE_UP_MS);
          });
        } else {
          navigate(done.go(ctx));
        }
        return true;
      }
      if (done.type !== "action") return false;
      // An action: do it for the visitor, then give up on it after a while.
      const before = placeOf(stateRef.current, tourId).stepIndex;
      performAction(done.action, step, memberId, toMakeRef.current);
      giveUpAfter(done.action === "solved" ? SOLVE_GIVE_UP_MS : ACTION_GIVE_UP_MS, () => {
        holdAdvance(placeKey, () => {
          const now = stateRef.current;
          if (placeOf(now, tourId).stepIndex === before && placeOf(now, tourId).status === "open") {
            next();
          }
        });
      });
      return true;
    };
    // The press goes as the pointer arrives on the press target, and at the
    // latest at ARRIVE_SHARE of the bar, when the step's glide ends.
    const midAt = barStart + full * ARRIVE_SHARE - PRESS_MARGIN_MS;
    let pressTimer = 0;
    const tryPress = () => {
      pressTimer = 0;
      if (autoplayRef.current !== "playing" || pressedRef.current === pressKey) return;
      const now = Date.now();
      const arrived = pointerArrival();
      if ((arrived?.press && now >= arrived.at) || now >= midAt) {
        pressNow();
        return;
      }
      const wait = arrived?.press ? arrived.at - now : 40;
      pressTimer = window.setTimeout(tryPress, Math.max(10, Math.min(wait, midAt - now)));
    };
    if (!needsPage && pressedRef.current !== pressKey && stepPressesOnArrival(step)) tryPress();
    let fired = false;
    const timer = window.setTimeout(() => {
      fired = true;
      pauseLeftRef.current = null;
      if (autoplayRef.current !== "playing") return;
      const s = stateRef.current;
      if (needsPage) {
        noteOldPage();
        setPendingNav(true);
        navigate(step.openPath?.(ctx) ?? "/");
        return;
      }
      // The action finished while the bar ran: the step moves on now.
      const pending = pendingAdvanceRef.current;
      if (pending?.key === placeKey) {
        pendingAdvanceRef.current = null;
        pending.run();
        return;
      }
      // The action is still on its way: the bar waits full until it finishes.
      if (pressedRef.current === pressKey) return;
      if (
        done.type === "action" &&
        done.action === "phone-opened" &&
        document.querySelector('[data-testid="demo-phone-view"]')
      ) {
        // Publish opened the phone already: the step is read, then it moves on.
        next();
      } else if (
        done.type === "next" &&
        step.anchors?.includes("demo-phone-toggle") &&
        document.querySelector('[data-testid="demo-phone-view"]')
      ) {
        // The phone opened as the step began (phoneFirst): it has been read.
        next();
      } else if (
        done.type === "next" &&
        step.anchors?.includes("demo-phone-toggle") &&
        (chapters[tourId]?.phoneMemberId ||
          memberId ||
          !document.querySelector('[data-testid="demo-phone-view"]'))
      ) {
        // "On the phone": open the phone on the scenario's own person (their
        // Inbox holds the update), then let it be read.
        const person = chapters[tourId]?.phoneMemberId ?? memberId;
        afterSidePanelsClose(() => {
          if (person) showDemoPhonePerson(person);
          else
            void pressAndWait(
              document.querySelector<HTMLElement>('[data-tour="demo-phone-toggle"]'),
            );
        });
        giveUpAfter(full, next);
      } else if (done.type === "action" && done.action === "scenario-opened" && scenarioLocked()) {
        // Change the scenario is locked (no solve yet): the step was read.
        next();
      } else if (done.type === "next") {
        // The introduction ends on its last card: Autoplay stops there (#2962).
        if (
          tourId === INTRO_ID &&
          placeOf(s, tourId).stepIndex + 1 >= stepCount(chapters, tourId)
        ) {
          setAutoplay("off");
        } else {
          next();
        }
      } else {
        // The press never went (the pointer had nowhere to go): it goes now.
        pressNow();
      }
    }, barLeft);
    return () => {
      window.clearTimeout(timer);
      window.clearTimeout(pressTimer);
      // Only a Pause keeps what is left. A wait cut short for another reason
      // (the page arrived, the card is not on screen yet) starts again in full:
      // a step never plays out its pause before its card shows (#2824).
      if (!fired && autoplayRef.current === "paused") {
        pauseLeftRef.current = {
          key: pauseKey,
          ms: Math.max(0, barLeft - (Date.now() - startedAt)),
        };
        // While paused, a finished action still waits for the bar.
        if (!needsPage) barEndRef.current = { key: placeKey, at: Number.POSITIVE_INFINITY };
      }
      // A Pause drops the presses still on their way (#2962): unless the step's
      // action has finished, Resume presses again from the start.
      if (
        autoplayRef.current !== "playing" &&
        pressedRef.current === pressKey &&
        pendingAdvanceRef.current?.key !== placeKey
      ) {
        pressedRef.current = "";
      }
    };
  }, [autoplay, placeKey, stepReady, routeOkShown, confirmSkip]);

  // The Autoplay pointer rests on the lit element of the step on show, and hides
  // when the step lights nothing or its card is out of sight (#2962).
  const cardOut =
    !visible ||
    (waiting && !chooser && !held) ||
    phoneGate ||
    changing ||
    (pendingNav && !routeOk) ||
    dialogBlocks ||
    underPhone;
  // No card on screen: the pointer hides. A card that lands moves it (onShown).
  const pointerOff = cardOut || chooser || !step || !routeOkShown || shownVisit !== visit;
  // biome-ignore lint/correctness/useExhaustiveDependencies: follow the step, not every render
  useEffect(() => {
    if (autoplay !== "playing") return;
    // After Autoplay pressed, the presses move the pointer (#2962).
    if (held) return;
    if (pointerOff) {
      followStep(null);
      return;
    }
    // Autoplay pressed on a card already on screen: go to its ring now.
    const shown = shownElRef.current;
    if (step && shown.visit === visit) followPointer(step, shown.el);
  }, [autoplay, pointerOff, visit, held]);
  // "Make the change": the change can arrive after the card landed. The pointer
  // then goes to the control the first press hits (#2962).
  const changeStep = step?.done.type === "action" && step.done.action === "change-made";
  // biome-ignore lint/correctness/useExhaustiveDependencies: follow when the change arrives
  useEffect(() => {
    if (autoplay !== "playing" || !changeStep || !toMake || !step || heldRef.current) return;
    const shown = shownElRef.current;
    if (shown.visit === visit) followPointer(step, shown.el);
  }, [toMake, changeStep, autoplay]);
  // A step about the phone: a side panel still open closes first, so its card can show.
  const drawerFirst =
    autoplay === "playing" && routeOk && step !== null && stepNeedsPhone(step) && drawerOpen;
  useEffect(() => {
    if (drawerFirst) closeSidePanels();
  }, [drawerFirst]);
  // "On the phone" with Autoplay: the phone opens on the scenario's person as
  // the step begins, and the card lands once, beside the open phone (#2824).
  const phoneFirst =
    autoplay === "playing" &&
    routeOk &&
    step?.done.type === "next" &&
    Boolean(step.anchors?.includes("demo-phone-toggle"));
  const phonePerson = tourId
    ? (chapters[tourId]?.phoneMemberId ?? chapters[tourId]?.memberId ?? null)
    : null;
  const [phoneWaitOver, setPhoneWaitOver] = useState(-1);
  // biome-ignore lint/correctness/useExhaustiveDependencies: once per step
  useEffect(() => {
    if (!phoneFirst) return;
    afterSidePanelsClose(() => {
      if (phonePerson) showDemoPhonePerson(phonePerson);
      else if (!isDemoPhoneOpen()) {
        void pressAndWait(document.querySelector<HTMLElement>('[data-tour="demo-phone-toggle"]'));
      }
    });
    const n = visit;
    const timer = window.setTimeout(() => setPhoneWaitOver(n), PHONE_OPEN_WAIT_MS);
    return () => window.clearTimeout(timer);
  }, [phoneFirst, placeKey]);
  const phoneComing = phoneFirst && !phoneOpen && phoneWaitOver !== visit;
  // Autoplay's Publish closes the Solve console before it presses Publish, and
  // the roster's buttons then move. It closes the console as the step begins
  // instead, so the card lands beside Publish where Publish stays (#2824).
  const publishFirst =
    autoplay === "playing" &&
    routeOk &&
    step?.done.type === "action" &&
    step.done.action === "published";
  // biome-ignore lint/correctness/useExhaustiveDependencies: once per step
  useEffect(() => {
    if (publishFirst) closeSidePanels();
  }, [publishFirst, placeKey]);

  if (!visible) return null;
  if (waiting && !chooser && !held) return null;
  if (phoneGate || changing) return null;

  let card: React.ReactNode;
  if (chooser) {
    // Continue: the introduction while it is unfinished, else this page's tour.
    const introOpen = placeOf(state, INTRO_ID).status !== "done";
    const pageTour = pageTourId(chapters, location.pathname);
    const scenario = state.activeScenario ? scenarioTourId(state.activeScenario) : null;
    const scenarioUnfinished =
      scenario !== null && chapters[scenario] && placeOf(state, scenario).status !== "done";
    const continueTour = introOpen ? INTRO_ID : scenarioUnfinished ? scenario : pageTour;
    card = (
      <GuideChooser
        canContinue={continueTour !== null}
        continueLabel={introOpen ? "Continue where I stopped" : "Show this page's tour"}
        onContinue={() => {
          bumpGate();
          setChooser(false);
          setWaiting(false);
          if (continueTour) commit(reopen(stateRef.current, continueTour), true);
        }}
        onRestart={() => {
          bumpGate();
          setChooser(false);
          commit(initialGuideState(), true);
        }}
        onClose={() => setChooser(false)}
        onAutoplay={(target) => {
          setChooser(false);
          requestDemoGuideAutoplay(target);
        }}
        hasPageTour={pageTour !== null}
      />
    );
  } else if (step && tourId) {
    // The page is never dimmed (#2824). While the anchor of a step on this page
    // arrives, the last card stays (the frame holds it). While another page
    // opens, no card shows on the old one.
    if (pendingNav && !routeOk) return null;
    if (autoplay === "playing" && !routeOkShown && step.openPath) return null;
    // Autoplay pressed and the page moved on under the card (a new page, the
    // lit control gone): the card stays where it was until its bar ends (#2962).
    const hold =
      (held && (!routeOk || !anchor.found)) ||
      (routeOk && (!anchor.ready || phoneComing || summaryHold));
    if (
      dialogBlocks ||
      underPhone ||
      (autoplay === "playing" && stepNeedsPhone(step) && drawerOpen)
    )
      return null;
    const total = stepCount(chapters, tourId);
    const done = step.done;
    const locked = done.type === "action" && done.action === "scenario-opened" && scenarioLocked();
    const onPlace = routeOk && anchor.found;
    let primary: { label: string; run: () => void; testId: string } | null = null;
    if (!routeOk && step.openPath) {
      primary = {
        label: step.openLabel ?? "Take me there",
        run: () => navigate(step.openPath?.(ctx) ?? "/"),
        testId: "demo-guide-open",
      };
    } else if (done.type === "next") {
      primary = {
        label: step.nextLabel ?? "Next",
        run: () => commit(goNext(stateRef.current, chapters, tourId), true),
        testId: "demo-guide-next",
      };
    } else if (done.type === "action" && step.pressLabel && onPlace) {
      // A control the card can press for the visitor (Open the scenarios,
      // #2962). While it is locked, the step only reads.
      primary = locked
        ? {
            label: step.nextLabel ?? "Next",
            run: () => commit(goNext(stateRef.current, chapters, tourId), true),
            testId: "demo-guide-next",
          }
        : {
            label: step.pressLabel,
            run: () => {
              const el =
                done.action === "scenario-opened"
                  ? scenarioButton()
                  : firstShownAnchor(step.anchors);
              el?.click();
            },
            testId: "demo-guide-press",
          };
    } else if (
      done.type === "action" &&
      done.nextWhenOnly &&
      firstShownAnchor([done.nextWhenOnly]) &&
      !firstShownAnchor(step.anchors?.filter((a) => a !== done.nextWhenOnly))
    ) {
      // The phone is already open: its button is gone, so never wait for a press.
      primary = {
        label: "Next",
        run: () => commit(goNext(stateRef.current, chapters, tourId), true),
        testId: "demo-guide-next",
      };
    } else if (!onPlace && done.type === "action") {
      // The element is not on the page: never leave the visitor stuck.
      primary = {
        label: "Next",
        run: () => commit(goNext(stateRef.current, chapters, tourId), true),
        testId: "demo-guide-next",
      };
    } else if (!onPlace && done.type === "route") {
      primary = {
        label: done.goLabel,
        run: () => navigate(done.go(ctx)),
        testId: "demo-guide-open",
      };
    }
    card = (
      <SpotlightFrame
        key={`${tourId}:${gateNonce}`}
        hold={hold}
        testIdPrefix="demo-guide"
        stepKey={`${tourId}:${step.id}${confirmSkip && !inScenario ? ":skip" : ""}${confirmLeave && inScenario ? ":leave" : ""}`}
        getElement={getElement}
        getClearOf={phonePanel}
        getAvoid={avoidDrawer}
        centered={!onPlace}
        onShown={onShown}
        onRingMove={onRingMove}
        passThrough
        surface="guide"
        onEscape={() => {
          if (!inScenario) setConfirmSkip((open) => !open);
          else if (confirmLeave) stayInScenario();
          else askLeave();
        }}
      >
        {({ titleId, bodyId }) =>
          confirmLeave && inScenario ? (
            <>
              <h2 id={titleId} className="text-base font-semibold tracking-tight">
                Leave the scenario?
              </h2>
              <p id={bodyId} className="mt-1 text-sm text-fg">
                The change you made stays as it is. The walkthrough for it ends.
              </p>
              <div className="mt-4 flex items-center justify-end gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={stayInScenario}
                  data-testid="demo-guide-leave-cancel"
                >
                  Stay
                </Button>
                <Button size="sm" onClick={leaveAndReplay} data-testid="demo-guide-leave-confirm">
                  Leave scenario
                </Button>
              </div>
            </>
          ) : confirmSkip && !inScenario ? (
            <>
              <h2 id={titleId} className="text-base font-semibold tracking-tight">
                Skip the tour?
              </h2>
              <p id={bodyId} className="mt-1 text-sm text-fg">
                This tour closes and you can explore on your own. Each page still shows its own tour
                the first time you open it. To see a tour again, choose Show the guide in the Help
                menu.
              </p>
              <div className="mt-4 flex items-center justify-end gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setConfirmSkip(false)}
                  data-testid="demo-guide-skip-cancel"
                >
                  Keep the tour
                </Button>
                <Button size="sm" onClick={closeGuide} data-testid="demo-guide-skip-confirm">
                  Skip tour
                </Button>
              </div>
            </>
          ) : (
            <>
              <p className="text-xs text-fg-muted" data-testid="demo-guide-count">
                Step {stepNumber(state, tourId)} of {total}
              </p>
              <h2 id={titleId} className="mt-1 text-base font-semibold tracking-tight">
                {step.title}
              </h2>
              <p id={bodyId} className="mt-1 whitespace-pre-line text-sm text-fg">
                {step.liveBody === "solve-changes" ? (
                  <SolveChangesBody waiting={step.body} />
                ) : (
                  renderMarks(
                    step.id === "make-change" && makeDetail
                      ? `${step.body}
${makeDetail}`
                      : locked && step.lockedBody
                        ? step.lockedBody
                        : step.body,
                  )
                )}
              </p>
              <div
                className="mt-4 flex flex-nowrap items-center justify-between gap-x-2"
                data-testid="demo-guide-actions"
              >
                <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
                  {inScenario ? (
                    <button
                      type="button"
                      onClick={leaveScenario}
                      className="rounded-sm text-xs text-fg-muted underline-offset-4 hover:text-fg hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                      data-testid="demo-guide-leave-scenario"
                    >
                      Leave scenario
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConfirmSkip(true)}
                      className="rounded-sm text-xs text-fg-muted underline-offset-4 hover:text-fg hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                      data-testid="demo-guide-skip"
                    >
                      Skip tour
                    </button>
                  )}
                  <AutoplayControls
                    mode={autoplay}
                    onPlay={() => setAutoplay("playing")}
                    onPause={() => setAutoplay("paused", true)}
                    onStop={() => setAutoplay("off", true)}
                  />
                </div>
                <div className="ml-auto flex shrink-0 flex-nowrap items-center justify-end gap-2">
                  {canGoBack(state, tourId) ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => commit(goBack(stateRef.current, tourId), true)}
                      data-testid="demo-guide-back"
                    >
                      Back
                    </Button>
                  ) : null}
                  {primary ? (
                    <Button size="sm" onClick={primary.run} data-testid={primary.testId}>
                      {primary.label}
                    </Button>
                  ) : null}
                </div>
              </div>
              {autoplay !== "off" && stepReady ? (
                <AutoplayProgress
                  key={placeKey}
                  paused={autoplay === "paused"}
                  durationMs={pauseShownMs}
                  waitsAfter={
                    (step.done.type === "action" || step.done.type === "route") &&
                    !pendingNav &&
                    advanceHeld !== placeKey
                  }
                />
              ) : null}
            </>
          )
        }
      </SpotlightFrame>
    );
  } else {
    return null;
  }
  return createPortal(card, document.body);
}

/** "N shifts changed." and the plain lines, or the waiting text until they arrive. */
export function solveChangesText(
  changes: { shifts_changed: number; lines: string[] } | null,
  waiting: string,
  stillComing = true,
): string {
  if (!changes) {
    return stillComing
      ? waiting
      : "Press Solve first. Then this step says which shifts moved and who took them.";
  }
  if (changes.shifts_changed === 0) return "The solve changed no shifts.";
  const count = `${changes.shifts_changed} ${changes.shifts_changed === 1 ? "shift" : "shifts"} changed.`;
  return [count, ...changes.lines.slice(0, 5)].join("\n");
}

/** The body of the "What the solve changed" step, read from the Solve's own changes. */
function SolveChangesBody({ waiting }: { waiting: string }) {
  const changes = useDemoSolveChanges();
  // Re-check once a second so the waiting line turns into a plain note if
  // nothing arrives (no Solve ran in this tab, or the read failed).
  const [, tick] = useState(0);
  useEffect(() => {
    if (changes) return;
    const id = window.setInterval(() => tick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, [changes]);
  return <>{solveChangesText(changes, waiting, solveChangesStillComing())}</>;
}

/**
 * A thin line along the bottom edge of the guide card (#2824, #2962). It fills
 * left to right over the one step duration, the same on every step, and stops
 * where it is while Autoplay pauses. Its end is the moment Autoplay presses or
 * moves on. A step whose action goes on after the press (a solve, a page that
 * loads) holds the line full and marks it as waiting, never a second speed.
 */
function AutoplayProgress({
  paused,
  durationMs,
  waitsAfter,
}: {
  paused: boolean;
  durationMs: number;
  waitsAfter: boolean;
}) {
  const [filled, setFilled] = useState(false);
  const waiting = waitsAfter && filled;
  return (
    <div
      aria-hidden="true"
      data-testid="demo-guide-progress"
      data-mode={waiting ? "waiting" : "counting"}
      data-paused={paused ? "true" : "false"}
      data-duration-ms={durationMs}
      className="pointer-events-none absolute inset-x-0 bottom-0 h-[3px] overflow-hidden rounded-b-lg bg-accent/15"
    >
      <style>
        {"@keyframes tarii-autoplay-fill{from{transform:scaleX(0)}to{transform:scaleX(1)}}"}
      </style>
      <div
        className="h-full bg-accent"
        onAnimationEnd={() => setFilled(true)}
        style={{
          width: "100%",
          transformOrigin: "left",
          // Fills left to right once and holds full: an action step waits at the end.
          animation: `tarii-autoplay-fill ${durationMs}ms linear forwards`,
          animationPlayState: paused ? "paused" : "running",
          opacity: waiting ? 0.6 : 1,
        }}
      />
    </div>
  );
}

/** Autoplay on the guide card: start, pause or resume, and stop. */
function AutoplayControls({
  mode,
  onPlay,
  onPause,
  onStop,
}: {
  mode: AutoplayState;
  onPlay: () => void;
  onPause: () => void;
  onStop: () => void;
}) {
  const link =
    "rounded-sm text-xs text-accent-fg underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent";
  if (mode === "off") {
    return (
      <button
        type="button"
        data-autoplay-control
        onClick={onPlay}
        className={link}
        data-testid="demo-guide-autoplay"
      >
        Autoplay
      </button>
    );
  }
  return (
    <span className="flex items-center gap-2" data-autoplay-control>
      <button
        type="button"
        onClick={mode === "playing" ? onPause : onPlay}
        className={link}
        data-testid={
          mode === "playing" ? "demo-guide-autoplay-pause" : "demo-guide-autoplay-resume"
        }
      >
        {mode === "playing" ? "Pause" : "Resume"}
      </button>
      <button
        type="button"
        onClick={onStop}
        className={link}
        data-testid="demo-guide-autoplay-stop"
      >
        Stop
      </button>
    </span>
  );
}
